import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api, BASE } from '../api/client';
import {
  useEnqueueKeyframeSequence,
  useKeyframeSequenceJobStatus,
} from '../hooks/useApi';

interface AssetUpload {
  id: string;
  bucketKey: string;
  thumbnailKey: string | null;
  mimeType: string;
}

/**
 * Wizard del pipeline avanzado video.long-keyframes.
 *
 * Flow:
 *  1. Subir N keyframes (mín. 2) — drag & drop O selector de archivos.
 *  2. Reordenar arrastrando (or moveUp/moveDown).
 *  3. Para cada par consecutivo: prompt de transición opcional.
 *  4. Configurar duración por clip + aspect ratio + modelo.
 *  5. "Generar" → backend dispara N-1 clips en paralelo + ffmpeg concat.
 *  6. Polling de status, video player al completar.
 */
export function KeyframeSequencePage() {
  const [keyframes, setKeyframes] = useState<AssetUpload[]>([]);
  const [movementPrompts, setMovementPrompts] = useState<string[]>([]);
  const [durationPerClip, setDurationPerClip] = useState(5);
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);

  const enqueue = useEnqueueKeyframeSequence();
  const status = useKeyframeSequenceJobStatus(jobId);

  // Mantener movementPrompts.length = keyframes.length - 1
  useEffect(() => {
    const need = Math.max(0, keyframes.length - 1);
    setMovementPrompts((prev) => {
      if (prev.length === need) return prev;
      const next = [...prev];
      while (next.length < need) next.push('');
      while (next.length > need) next.pop();
      return next;
    });
  }, [keyframes.length]);

  const onFilesSelected = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadError(null);
    try {
      // Necesitamos el slug de un proyecto para subir vía /projects/:slug/assets.
      // Para simplificar, usamos el proyecto interno '__character-gallery'.
      const uploaded: AssetUpload[] = [];
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('kind', 'reference');
        const res = await fetch(`${BASE}/projects/__character-gallery/assets`, {
          method: 'POST',
          credentials: 'include',
          body: fd,
        });
        if (!res.ok) {
          throw new Error(`Upload falló (${file.name}): HTTP ${res.status}`);
        }
        const asset = (await res.json()) as AssetUpload;
        uploaded.push(asset);
      }
      setKeyframes((prev) => [...prev, ...uploaded]);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  };

  const moveKeyframe = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= keyframes.length) return;
    const next = [...keyframes];
    [next[index], next[target]] = [next[target], next[index]];
    setKeyframes(next);
    // También mover el prompt correspondiente para mantener correspondencia
    const nextPrompts = [...movementPrompts];
    if (index < nextPrompts.length && target < nextPrompts.length) {
      [nextPrompts[index], nextPrompts[target]] = [nextPrompts[target], nextPrompts[index]];
      setMovementPrompts(nextPrompts);
    }
  };

  const removeKeyframe = (index: number) => {
    setKeyframes((prev) => prev.filter((_, i) => i !== index));
  };

  const submit = async () => {
    if (keyframes.length < 2) {
      setUploadError('Necesitás al menos 2 keyframes');
      return;
    }
    setUploadError(null);
    try {
      const result = await enqueue.mutateAsync({
        keyframeAssetIds: keyframes.map((k) => k.id),
        movementPrompts,
        durationPerClip,
        aspectRatio,
      });
      setJobId(result.jobId);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    }
  };

  const reset = () => {
    setKeyframes([]);
    setMovementPrompts([]);
    setJobId(null);
    setUploadError(null);
  };

  const totalDuration = (keyframes.length - 1) * durationPerClip;
  const estimatedCost = totalDuration * 0.052; // $0.052/s Seedance Pro

  // Si hay job en curso o completado, mostrar vista de progreso
  if (jobId && status.data) {
    return (
      <KeyframeSequenceProgress
        status={status.data}
        keyframes={keyframes}
        onReset={reset}
      />
    );
  }

  return (
    <div className="p-3 sm:p-6 max-w-5xl mx-auto">
      <header className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold text-slate-100">🎞 Video largo por keyframes</h1>
          <Link to="/projects" className="text-xs text-slate-400 hover:text-vision underline">
            ← proyectos
          </Link>
        </div>
        <p className="text-sm text-slate-400">
          Subí <strong>2 o más keyframes</strong> (imágenes) ordenados. El sistema genera
          <strong> N-1 clips Seedance Pro</strong> en paralelo (image_url + end_image_url), después
          los une con ffmpeg en un solo video largo. Sin límite de duración.
        </p>
      </header>

      {/* Upload zone */}
      <section className="bg-slate-900 border border-slate-700 rounded-lg p-4 mb-4">
        <h2 className="text-xs uppercase text-slate-400 mb-3">📥 Keyframes ({keyframes.length})</h2>

        <input
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => onFilesSelected(e.target.files)}
          disabled={uploading}
          className="block w-full text-sm text-slate-300 file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:bg-slate-800 file:text-slate-200 hover:file:bg-slate-700 file:cursor-pointer mb-3"
        />
        {uploading && <div className="text-xs text-slate-500">Subiendo…</div>}
        {uploadError && (
          <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2 mb-2">
            {uploadError}
          </div>
        )}

        {keyframes.length === 0 ? (
          <div className="border border-dashed border-slate-700 rounded p-6 text-center text-slate-500 text-sm">
            Sin keyframes. Subí imágenes para arrancar.
          </div>
        ) : (
          <div className="space-y-2">
            {keyframes.map((k, i) => (
              <div key={k.id} className="flex items-center gap-2 bg-slate-950 border border-slate-800 rounded p-2">
                <span className="text-xs text-slate-500 w-6 text-center">K{i + 1}</span>
                <img
                  src={`${BASE}/assets/${k.id}/thumb`}
                  alt={`K${i + 1}`}
                  className="w-16 h-16 object-cover rounded border border-slate-800"
                />
                <div className="flex-1 min-w-0">
                  {i < keyframes.length - 1 && (
                    <input
                      type="text"
                      value={movementPrompts[i] ?? ''}
                      onChange={(e) => {
                        const next = [...movementPrompts];
                        next[i] = e.target.value;
                        setMovementPrompts(next);
                      }}
                      placeholder={`Movimiento K${i + 1} → K${i + 2} (opcional, ej: "dolly forward smooth")`}
                      className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200 focus:outline-none focus:border-vision"
                    />
                  )}
                  {i === keyframes.length - 1 && (
                    <span className="text-[10px] text-slate-600 italic">último frame</span>
                  )}
                </div>
                <div className="flex flex-col gap-0.5">
                  <button
                    onClick={() => moveKeyframe(i, -1)}
                    disabled={i === 0}
                    className="text-xs text-slate-500 hover:text-slate-200 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Subir"
                  >
                    ▲
                  </button>
                  <button
                    onClick={() => moveKeyframe(i, 1)}
                    disabled={i === keyframes.length - 1}
                    className="text-xs text-slate-500 hover:text-slate-200 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Bajar"
                  >
                    ▼
                  </button>
                </div>
                <button
                  onClick={() => removeKeyframe(i)}
                  className="text-xs text-slate-600 hover:text-red-400"
                  title="Quitar"
                >
                  🗑
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Configuración */}
      {keyframes.length >= 2 && (
        <section className="bg-slate-900 border border-slate-700 rounded-lg p-4 mb-4">
          <h2 className="text-xs uppercase text-slate-400 mb-3">⚙ Configuración</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400 block mb-1">
                Duración por clip ({durationPerClip}s)
              </label>
              <input
                type="range"
                min={4}
                max={12}
                value={durationPerClip}
                onChange={(e) => setDurationPerClip(parseInt(e.target.value, 10))}
                className="w-full"
              />
              <div className="flex justify-between text-[10px] text-slate-600">
                <span>4s</span>
                <span>12s</span>
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1">Aspect ratio</label>
              <select
                value={aspectRatio}
                onChange={(e) => setAspectRatio(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-100"
              >
                <option value="16:9">16:9 horizontal</option>
                <option value="9:16">9:16 vertical (reels)</option>
                <option value="1:1">1:1 cuadrado</option>
                <option value="21:9">21:9 cinematográfico</option>
              </select>
            </div>
          </div>
          <div className="mt-3 text-xs text-slate-400 space-y-1">
            <div>
              📊 <strong>{keyframes.length - 1} clips</strong> de {durationPerClip}s ={' '}
              <strong>{totalDuration}s</strong> total
            </div>
            <div>
              💰 Costo estimado: <strong className="text-amber-400">~${estimatedCost.toFixed(2)}</strong>{' '}
              ({keyframes.length - 1} × {durationPerClip}s × $0.052/s)
            </div>
            <div>
              ⏱ Tiempo estimado: ~{Math.max(1, Math.ceil(durationPerClip * 0.5))}-{durationPerClip * 2} min
              (clips en paralelo)
            </div>
          </div>
        </section>
      )}

      {/* Botón generar */}
      {keyframes.length >= 2 && (
        <div className="flex justify-end gap-2">
          <button
            onClick={reset}
            disabled={uploading || enqueue.isPending}
            className="px-4 py-2 text-sm rounded bg-slate-800 hover:bg-slate-700 text-slate-300 disabled:opacity-40"
          >
            Limpiar
          </button>
          <button
            onClick={submit}
            disabled={uploading || enqueue.isPending}
            className="px-4 py-2 text-sm rounded bg-vision-dark hover:bg-vision text-white disabled:opacity-40"
          >
            {enqueue.isPending ? 'Enviando…' : `🎬 Generar video (${totalDuration}s)`}
          </button>
        </div>
      )}
    </div>
  );
}

function KeyframeSequenceProgress({
  status,
  keyframes,
  onReset,
}: {
  status: import('../hooks/useApi').KeyframeSequenceJobStatus;
  keyframes: AssetUpload[];
  onReset: () => void;
}) {
  const pct =
    status.clipsExpected > 0 ? (status.clipsGenerated / status.clipsExpected) * 100 : 0;

  return (
    <div className="p-3 sm:p-6 max-w-5xl mx-auto">
      <header className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold text-slate-100">🎞 Generando video largo…</h1>
          <button
            onClick={onReset}
            className="text-xs text-slate-400 hover:text-vision underline"
          >
            ← nuevo video
          </button>
        </div>
      </header>

      <section className="bg-slate-900 border border-slate-700 rounded-lg p-4 mb-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <Stat label="Estado" value={statusEmoji(status.status)} />
          <Stat label="Keyframes" value={String(status.keyframesCount)} />
          <Stat
            label="Clips"
            value={`${status.clipsGenerated} / ${status.clipsExpected}`}
          />
          <Stat
            label="Costo"
            value={status.costUsd != null ? `$${status.costUsd.toFixed(2)}` : '—'}
          />
        </div>

        {/* Progress bar */}
        {status.status !== 'failed' && (
          <div className="bg-slate-950 border border-slate-800 rounded h-3 overflow-hidden mb-2">
            <div
              className={`h-full transition-all ${
                status.status === 'completed' ? 'bg-emerald-500' : 'bg-vision'
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
        <div className="text-xs text-slate-400">
          {status.status === 'pending' && 'Encolado, arrancando…'}
          {status.status === 'generating' &&
            `Generando clips en paralelo (Seedance Pro). ${status.clipsGenerated}/${status.clipsExpected} listos.`}
          {status.status === 'concatenating' && 'Concatenando con ffmpeg…'}
          {status.status === 'completed' && '✅ Listo'}
          {status.status === 'failed' && (
            <span className="text-red-400">❌ Error: {status.errorMessage}</span>
          )}
        </div>
      </section>

      {status.status === 'completed' && status.finalVideoAssetId && (
        <section className="bg-slate-900 border border-slate-700 rounded-lg p-4">
          <h2 className="text-xs uppercase text-slate-400 mb-2">▶️ Video final</h2>
          <video
            controls
            autoPlay
            src={`${BASE}/assets/${status.finalVideoAssetId}/proxy`}
            className="w-full rounded border border-slate-700 bg-slate-950"
          />
          <div className="text-[10px] text-slate-500 mt-2">
            Asset id: <code>{status.finalVideoAssetId}</code>
          </div>
        </section>
      )}

      {/* Thumbnails de keyframes */}
      {keyframes.length > 0 && (
        <section className="mt-4">
          <h2 className="text-xs uppercase text-slate-400 mb-2">📸 Keyframes usados</h2>
          <div className="flex gap-2 flex-wrap">
            {keyframes.map((k, i) => (
              <div key={k.id} className="text-center">
                <img
                  src={`${BASE}/assets/${k.id}/thumb`}
                  alt={`K${i + 1}`}
                  className="w-20 h-20 object-cover rounded border border-slate-700"
                />
                <div className="text-[10px] text-slate-500 mt-1">K{i + 1}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-slate-950 border border-slate-800 rounded p-3">
      <div className="text-[10px] text-slate-500 uppercase">{label}</div>
      <div className="text-base font-semibold text-slate-100 mt-0.5">{value}</div>
    </div>
  );
}

function statusEmoji(status: string): string {
  switch (status) {
    case 'pending':
      return '⏳ encolado';
    case 'generating':
      return '🎬 generando';
    case 'concatenating':
      return '🔗 uniendo';
    case 'completed':
      return '✅ listo';
    case 'failed':
      return '❌ falló';
    default:
      return status;
  }
}

// Re-export para suprimir warning de import
export type { AssetUpload };
// imports requeridos por TypeScript pero no usados visualmente
void api;
